import classNames from 'classnames';
import isNumber from 'lodash/isNumber';
import React, { ReactNode, CSSProperties } from 'react';
import { Link } from 'wouter';

import BasicButton from '../buttons/BasicButton';

import styles from '../../styles/cards/vertical-card.module.css';

export interface VerticalCardProps {
    href?: string | null;
    external?: boolean;
    target?: string;
    thumbnail?: ReactNode;
    thumbnailRatio?: number | null;
    width?: string | number | null;
    height?: string | number | null;
    children?: ReactNode;
    footer?: ReactNode;
    style?: CSSProperties | null;
    className?: string | null;
    thumbnailClassName?: string | null;
    labelClassName?: string | null;
    innerClassName?: string | null;
    footerClassName?: string | null;
    labelOnTop?: boolean;
    onClick?: ((e: React.MouseEvent) => void) | null;
}

function VerticalCard({
    href = null,
    external = false,
    target = '_blank',
    thumbnail = null,
    thumbnailRatio = null,
    width = null,
    height = null,
    children = null,
    footer = null,
    style = null,
    className = null,
    thumbnailClassName = null,
    labelClassName = null,
    innerClassName = null,
    footerClassName = null,
    labelOnTop = false,
    onClick = null,
}: VerticalCardProps) {
    const thumbnailStyle = {
        width: width || '100%',
        height: height || (isNumber(width) ? width / (thumbnailRatio as number) : undefined),
        paddingBottom:
            width === null && height === null && thumbnailRatio !== null
                ? `${(1 / (thumbnailRatio as number)) * 100}%`
                : null,
    };

    const Element = href !== null || onClick !== null ? 'span' : 'div';

    const inner = (
        <Element className={classNames([styles.inner, innerClassName])}>
            {labelOnTop ? (
                <Element className={classNames([styles.label, labelClassName])}>{children}</Element>
            ) : null}
            <Element
                className={classNames([
                    styles.thumbnail,
                    {
                        'contain-size': thumbnailStyle.paddingBottom !== null,
                    },
                    thumbnailClassName,
                ])}
                style={thumbnailStyle}
            >
                {thumbnail}
            </Element>
            {!labelOnTop ? (
                <Element className={classNames([styles.label, labelClassName])}>{children}</Element>
            ) : null}
            {footer !== null ? (
                <Element className={classNames([styles.footer, footerClassName])}>{footer}</Element>
            ) : null}
        </Element>
    );

    if (onClick !== null || href !== null) {
        return (
            <BasicButton
                href={href}
                external={external}
                className={classNames([styles.container, className])}
                onClick={onClick}
                style={style}
            >
                {inner}
            </BasicButton>
        );
    }

    return (
        <div className={classNames([styles.container, className])} style={style}>
            {inner}
        </div>
    );
}

export default VerticalCard;
